import { useCallback, useEffect } from 'react';
import useICS2Codelists from './useICS2Codelists';

/**
 * Resolves ICS2 codelist values to their descriptions, preloading the lists it will be asked about.
 *
 * Customs sends bare codes (an additional-information code, a screening method), so the meaning has
 * to come from the codelist. Falls back to the code itself while the list is loading or when
 * customs sends a value the list does not contain.
 */
const useIcs2CodelistLabel = (codelistNames: string[]) => {
    const { ics2Codelists, preloadICS2Codelists } = useICS2Codelists();

    // Callers pass a fresh array literal each render, so depend on its contents, not its identity.
    const names = codelistNames.join(',');

    useEffect(() => {
        preloadICS2Codelists(names.split(','));
    }, [names, preloadICS2Codelists]);

    return useCallback(
        (code: string | undefined | null, codelistName: string, withCode = false): string => {
            if (!code) return '-';
            const description = ics2Codelists?.[codelistName]?.find((item) => item.id === code)?.value;
            if (!description) return code;
            return withCode ? `${code} - ${description}` : description;
        },
        [ics2Codelists]
    );
};

export default useIcs2CodelistLabel;
